import { computed, useSignal, type Signal } from '@preact/signals';
import { createContext, type ComponentChildren } from 'preact';
import { useContext } from 'preact/hooks';

const QueryStringStoreContext = createContext<any | undefined>(undefined);

export type QueryStringStore = Record<string, string | string[] | undefined>;

export type QueryStringStoreSignals<T extends QueryStringStore> = {
  [K in keyof Required<T>]: Signal<T[K]>;
};

export type QueryStringStoreProviderProps = {
  children: ComponentChildren;
  namespace: string;
  navigate: (path: string) => void;
  location?: Location;
};

/**
 * Stores Application state in the URL search params (query string) using a Proxy with Preact Signals.
 *
 * Note: Values are always string or string[]
 *
 * Each query param is an individual `computed` signal to reduce unnecessary re-renders, allows
 * components to react only to the specific query params they depend on.
 */
export function QueryStringStoreProvider<State extends QueryStringStore>({
  children,
  namespace,
  navigate,
  location
}: QueryStringStoreProviderProps) {
  const state = useSignal<QueryStringStore>(
    stateFromQueryString(namespace, location?.search)
  );

  function setQueryStringState(stateMod: State): void {
    const newState = {
      ...state.value,
      ...stateMod
    };

    navigate(queryStringFromState(namespace, newState, location?.search));
    state.value = newState;
  }

  const computedStateProxy = new Proxy(state, {
    get: (target, prop: string) => computed(() => target.value[prop])
  });

  return (
    <QueryStringStoreContext.Provider
      // Note: This component is not re-rendered, no need to memoize
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={[computedStateProxy, setQueryStringState]}
    >
      {children}
    </QueryStringStoreContext.Provider>
  );
}

export default function useQueryStringStore<State extends QueryStringStore>(): [
  QueryStringStoreSignals<State>,
  (state: State) => void
] {
  const context = useContext(QueryStringStoreContext);

  if (!context) {
    throw new Error(
      'useQueryStringStore must be used within QueryStringStoreProvider'
    );
  }

  return context;
}

function stateFromQueryString(
  namespace: string,
  search?: string
): QueryStringStore {
  if (!search) return {};

  const params = new URLSearchParams(search);

  const states = [...params.keys()]
    .filter((key) => key.split('_')[0] === namespace)
    .map((key) => [key.split('_')[1], decode(params.getAll(key))])
    .map(([key, value]) => [key, value.length === 1 ? value[0] : value]);

  return Object.fromEntries(states);
}

function queryStringFromState(
  namespace: string,
  state: QueryStringStore,
  search?: string
): string {
  const states = [...Object.entries(state)].map(([key, value]) => [
    `${namespace}_${key}`,
    value !== undefined ? encode(value) : value
  ]) as [string, string | string[] | undefined][];
  const params = new URLSearchParams(search);

  states.forEach(([key, value]) => {
    if (value === undefined) {
      params.delete(key);
    } else if (Array.isArray(value)) {
      params.delete(key);
      for (const val of value) {
        params.append(key, val);
      }
    } else {
      params.set(key, value);
    }
  });

  const paramsString = params.toString();

  return paramsString.length > 0 ? `?${paramsString}` : '';
}

function encode(value: string | string[]): string | string[] {
  if (Array.isArray(value)) {
    return value.map((val) => encodeURIComponent(val));
  }

  return encodeURIComponent(value);
}

function decode(value: string | string[]): string | string[] {
  if (Array.isArray(value)) {
    return value.map((val) => decodeURIComponent(val));
  }

  return decodeURIComponent(value);
}
